import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useGetTelehealthSessionInfoCallback } from '../../../../hooks';
import { useVideoPlatformCallback } from '../../../../hooks/useVideoPlatform';
import { useTrackEvent } from '../../../../segment/segment';
import { EventTypeEnum } from '../../../../segment/types';
import { AppState } from '../../../../types';
import { trySafely } from '../../../../utils';
import { normalizeVideoPlatform } from '../../../../video-sdk';
import { admissionTrackingHistory } from '../analytics/AdmissionTrackingHistory';

export function useAdmissionSystemNotificationsTrackingEvents() {
  const { userViewed, userClicked } = useTrackEvent();
  const { patientShortId, providerShortId } = useParams<{
    patientShortId: string;
    providerShortId: string;
  }>();
  const [getTelehealthSessionInfo] = useGetTelehealthSessionInfoCallback();
  const getVideoPlatform = useVideoPlatformCallback();

  return useMemo(() => {
    const getTrackingPayload = async () => {
      const { data: telehealthSessionInfoData } =
        patientShortId || providerShortId
          ? ((await trySafely(
              getTelehealthSessionInfo({
                variables: {
                  providerShortId,
                  patientShortId,
                },
              }),
            )) ?? {})
          : {};

      return {
        patientShortId: patientShortId ?? '',
        providerShortId: providerShortId ?? '',
        appointmentShortId: telehealthSessionInfoData?.telehealthSessionInfo?.appointment?.shortId ?? '',
        source: normalizeVideoPlatform(getVideoPlatform()) ?? '',
        componentName: AppState.WAITING,
        componentType: 'page',
        txId: admissionTrackingHistory.getTransactionId() ?? '',
      };
    };

    return {
      trackSystemNotificationForAdmissionViewed: async () =>
        userViewed(EventTypeEnum.SYSTEM_NOTIFICATION_FOR_ADMISSION_VIEWED, await getTrackingPayload()),
      trackSystemNotificationForAdmissionClicked: async () =>
        userClicked(EventTypeEnum.SYSTEM_NOTIFICATION_FOR_ADMISSION_CLICKED, await getTrackingPayload()),
    };
  }, [patientShortId, providerShortId, getTelehealthSessionInfo, getVideoPlatform, userViewed, userClicked]);
}
